import { _debug } from '$extensionSrc/utils/logging'

import {
  getWaitForElementToAppearPromise,
  wait,
} from '../interruptiblePromises'
import { evaluateXpath } from '../interruptibleXpath'
import { getElement, getOptionalElement } from '../utils'

export function getPostId(postDiv: HTMLDivElement): string {
  const urn = postDiv.dataset.urn
  if (urn) {
    const match = urn.match(/urn:li:activity:(\d+)/)
    if (match) {
      return match[1]
    }
  }
  throw new Error('Could not extract post ID from post element')
}

export function getPostTimestampFromPostId(postId: string): number {
  const num = BigInt(postId)
  const binary = num.toString(2).padStart(64, '0')
  return parseInt(binary.substring(0, 42), 2)
}

export function getPostTimestamp(postDiv: HTMLDivElement): number {
  try {
    const postId = getPostId(postDiv)
    const timestamp = getPostTimestampFromPostId(postId)
    return timestamp
  } catch (error) {
    _debug(
      `Failed to extract timestamp from post: ${error}`,
      'linkedinPostUtils.getPostTimestamp',
      'e',
    )
    throw error
  }
}

async function getHasBeenDeleted(postDiv: HTMLDivElement): Promise<boolean> {
  const removedSpan = await getOptionalElement(
    postDiv,
    './/div[@role="alert"]/descendant::span[contains(., "removed")]',
    { timeout: 1000 },
  )
  return !!removedSpan
}

export async function deletePost(
  post: HTMLDivElement,
  isDry: boolean,
): Promise<void> {
  const menuButton: HTMLButtonElement = await getElement(post, './/button')
  menuButton.click()
  await wait(1000)

  const menuOptions: HTMLUListElement = await getElement(
    menuButton,
    './/following-sibling::div[@aria-label="Control Menu Options"]/descendant::ul',
  )

  const deleteOption: HTMLButtonElement = await getElement(
    menuOptions,
    './/li[contains(@class, "option-delete")]/div[@role="button"]',
  )
  deleteOption.click()
  await wait(1000)

  const confirmDelete: HTMLButtonElement = await getElement(
    document,
    '//div[@role="dialog"]/descendant::button[text()="Delete"]',
  )

  if (!isDry) {
    confirmDelete.click()
  } else {
    _debug(
      'Dry run: LinkedIn post deletion not performed',
      'linkedinDeletePostsBefore.deletePost',
      'd',
    )
    const cancelButton: HTMLButtonElement = await getElement(
      document,
      '//div[@role="dialog"]/descendant::button[text()="Cancel"]',
    )
    cancelButton.click()
  }
}

async function getPostList(): Promise<HTMLUListElement> {
  // This main UL element contains the list of all posts on the page.
  // As more posts are loaded, the UL child element list grows.
  return getWaitForElementToAppearPromise<HTMLUListElement>(
    document,
    () => evaluateXpath<HTMLUListElement>(document, '//main//ul')[0],
  )
}

async function scrollAndGetPostFromListElement(
  postListElement: HTMLLIElement,
): Promise<HTMLDivElement> {
  // Posts must be scrolled into view before the page loads the content.
  postListElement.scrollIntoView({ behavior: 'smooth', block: 'end' })
  const postDiv = await getWaitForElementToAppearPromise<HTMLDivElement>(
    postListElement,
    () =>
      evaluateXpath<HTMLDivElement>(
        postListElement,
        './/div[@role="article"]',
      )[0],
  )
  if (!postDiv) {
    throw new Error('Post div not found in list element')
  }
  return postDiv
}

async function maybeClickShowMoreResultsButton(): Promise<boolean> {
  // LinkedIn seems to load 20 posts at a time, with a button to load more posts
  // at the bottom. Clicking this will load more posts into the post list.
  // If we've already loaded all posts, the button will not be present.
  const showMoreResultsButton: HTMLButtonElement | null =
    await getOptionalElement(
      document,
      '//span[text()="Show more results"]/parent::button',
    )
  if (showMoreResultsButton) {
    showMoreResultsButton.click()
    _debug(
      'Clicked show more results button',
      'linkedinDeletePostsBefore.maybeClickShowMoreResultsButton',
      'd',
    )
    await wait(1000)
    return true
  }
  return false
}

function setupPostListObserver(
  postList: HTMLUListElement,
  postListElements: HTMLLIElement[],
): MutationObserver {
  const callback = (mutations: MutationRecord[]) => {
    for (const mutation of mutations) {
      for (const addedNode of mutation.addedNodes) {
        if (addedNode instanceof HTMLLIElement) {
          postListElements.push(addedNode)
        }
      }
    }
  }
  const observer = new MutationObserver(callback)
  observer.observe(postList, { childList: true })
  return observer
}

export async function* getPostsIterator(olderThanTimestamp: number) {
  _debug(
    `Starting LinkedIn posts iteration with olderThanTimestamp ${olderThanTimestamp}`,
    'linkedinDeletePostsBefore.getPostsIterator',
    'd',
  )

  const postList = await getPostList()
  if (!postList || postList.children.length === 0) {
    _debug('No posts found', 'linkedinDeletePostsBefore.getPostsIterator', 'd')
    return
  }

  const postListElements: HTMLLIElement[] = Array.from(
    postList.children,
  ) as HTMLLIElement[]
  const observer = setupPostListObserver(postList, postListElements)
  let hasLoadedMorePosts = false

  try {
    while (postListElements.length > 0 || !hasLoadedMorePosts) {
      if (postListElements.length === 0) {
        const didClickButton = await maybeClickShowMoreResultsButton()
        if (didClickButton) {
          hasLoadedMorePosts = true
        } else {
          _debug(
            'No more posts to delete',
            'linkedinDeletePostsBefore.getPostsIterator',
            'd',
          )
          break
        }
      } else {
        hasLoadedMorePosts = false
        const postListElement = postListElements.shift()
        if (!postListElement) {
          _debug(
            'Reached end of post list',
            'linkedinDeletePostsBefore.getPostsIterator',
            'd',
          )
          break
        }
        const postDiv = await scrollAndGetPostFromListElement(postListElement)
        if (!postDiv) {
          throw new Error('No post div found in list element')
        }

        const hasBeenDeleted = await getHasBeenDeleted(postDiv)
        if (hasBeenDeleted) {
          _debug(
            `Post ${postDiv.dataset.urn} has already been deleted, skipping`,
            'linkedinDeletePostsBefore.getPostsIterator',
            'd',
          )
        } else {
          const postTimestamp = getPostTimestamp(postDiv)
          if (postTimestamp < olderThanTimestamp) {
            yield postDiv
          } else {
            _debug(
              `Post ${postDiv.dataset.urn} is not older than ${olderThanTimestamp}, skipping`,
              'linkedinDeletePostsBefore.getPostsIterator',
              'd',
            )
          }
        }
      }
    }
  } finally {
    observer.disconnect()
  }
}
